import type { RootState } from "../../../store.ts"
import type { CalendarPermissions, Event } from "../types/types.ts"

type Permissions = RootState["auth"]["permissions"]

/** Whether the signed-in user can edit calendar events directly — the single
 *  source every caller reads (the Calendar page's own gate, and any
 *  deep-linking caller such as the Lead Workspace's appointment Edit
 *  action), so the two can't diverge. */
export const calendarEditAllowedFromPermissions = (
  permissions: Permissions | undefined
): boolean => !!permissions?.codes?.calendar?.calendar?.calendar_edit

export const selectCalendarEditAllowed = (state: RootState): boolean =>
  calendarEditAllowedFromPermissions(state.auth?.permissions)

/** Whether the signed-in user is this event's own creator — the one place
 *  reminder ownership is decided (CalendarViewReminder's `isView` prop and
 *  `canEditCalendarEvent` below both read it, rather than each re-deriving
 *  `userId === event.created_by.id` independently). */
export const isOwnCalendarEvent = (
  event: Pick<Event, "created_by"> | undefined,
  userId: string | undefined
): boolean => userId === event?.created_by?.id

/** The one rule for whether this event's Edit action is offered at all —
 *  edit permission, plus ownership for a reminder. */
export const canEditCalendarEvent = (
  permissions: CalendarPermissions | undefined,
  event: Pick<Event, "type" | "created_by"> | undefined,
  userId: string | undefined
): boolean => {
  const isReminder = event?.type?.toLowerCase() === "reminder"
  return !!permissions?.edit && (!isReminder || isOwnCalendarEvent(event, userId))
}

/** The one rule for whether an existing appointment's Edit action deep-links
 *  into the Calendar edit view instead of falling back to the caller's own
 *  scoped editor — read identically by the Lead Workspace header and the
 *  Leads Active table, so the two can't drift the way the original bug did. */
export const canDeepLinkAppointmentEdit = (
  canEditLead: boolean | undefined,
  calendarEditAllowed: boolean,
  appointmentDate: string | null | undefined,
  appointmentEventId: string | null | undefined
): boolean =>
  !!canEditLead &&
  calendarEditAllowed &&
  !!appointmentDate &&
  !!appointmentEventId
